package ai.wagemix.net.client

import com.fsryan.foundation.net.client.FSRequest
import com.fsryan.foundation.net.client.FSRequestBuilder
import com.fsryan.foundation.net.client.acceptJson
import com.fsryan.foundation.net.client.addQueryParam
import com.fsryan.foundation.net.client.queryParamValues
import com.fsryan.foundation.net.client.replacePathParams

abstract class WMOccupationStatsRequestBuilder<B: WMOccupationStatsRequestBuilder<B>> protected constructor(
    private val format: String
): WMYearBoundRequestBuilder<B>(yearBoundsRequired = false) {

    private var areaId: Int? = null
    private var industryId: Int? = null
    private val occupationIds = mutableListOf<Int>()
    private val weights = mutableListOf<Int>()

    /** The area filter the request carries so far, or `null` for all areas. */
    val requestedAreaId: Int? get() = areaId

    /** The industry filter the request carries so far, or `null` for all industries. */
    val requestedIndustryId: Int? get() = industryId

    /** The occupations added so far, each with its weight, in insertion order. */
    val requestedOccupations: List<Pair<Int, Int>> get() = occupationIds.zip(weights)

    fun areaId(areaId: Int): B {
        this.areaId = areaId
        return self()
    }

    fun industryId(industryId: Int): B {
        this.industryId = industryId
        return self()
    }

    fun addOccupationId(occupationId: Int, weight: Int): B {
        occupationIds.add(occupationId)
        weights.add(weight)
        return self()
    }

    override fun applyFrom(request: FSRequest<Nothing>): B {
        request.queryParamValues(QUERY_PARAM_AREA_ID).firstOrNull()?.let { areaId = it.toInt() }
        request.queryParamValues(QUERY_PARAM_INDUSTRY_ID).firstOrNull()?.let { industryId = it.toInt() }
        request.queryParamValues(QUERY_PARAM_OCCUPATION_ID).mapNotNull { it.toIntOrNull() }.forEach { occupationId ->
            occupationIds.add(occupationId)
        }
        request.queryParamValues(QUERY_PARAM_WEIGHT).mapNotNull { it.toIntOrNull() }.forEach { weight ->
            weights.add(weight)
        }
        return super.applyFrom(request)
    }

    override fun build(): FSRequest<Nothing> {
        method(FSRequest.Method.Get)
        self().acceptJson()
        self().replacePathParams("api", "v1", "chart")
        if (areaId != null) {
            self().addQueryParam(name = QUERY_PARAM_AREA_ID, value = areaId.toString())
        }
        if (industryId != null) {
            self().addQueryParam(name = QUERY_PARAM_INDUSTRY_ID, value = industryId.toString())
        }
        if (occupationIds.isEmpty()) {
            error("At least one occupation ID must be provided")
        }
        occupationIds.forEach { occupationId ->
            self().addQueryParam(name = QUERY_PARAM_OCCUPATION_ID, value = occupationId.toString())
        }
        if (weights.isEmpty()) {
            error("At least one weight must be provided")
        }
        if (weights.size != occupationIds.size) {
            error("Number of occupation IDs (o: ${occupationIds.size}) must match number of weights (w: ${weights.size})")
        }
        weights.forEach { weight ->
            self().addQueryParam(name = QUERY_PARAM_WEIGHT, value = weight.toString())
        }
        self().addQueryParam(name = QUERY_PARAM_FORMAT, value = format)
        return super.build()
    }

    override fun clear(): B {
        areaId = null
        industryId = null
        occupationIds.clear()
        return super.clear()
    }

    companion object {
        const val QUERY_PARAM_AREA_ID = "a"
        const val QUERY_PARAM_INDUSTRY_ID = "i"
        const val QUERY_PARAM_OCCUPATION_ID = "o"
        const val QUERY_PARAM_WEIGHT = "w"
        const val QUERY_PARAM_FORMAT = "format"
    }
}

class WMOccupationStatsCollectionRequestBuilder(): WMOccupationStatsRequestBuilder<WMOccupationStatsCollectionRequestBuilder>(
    "stats_collection"
) {
    override fun self(): WMOccupationStatsCollectionRequestBuilder = this
}

class WMOccupationStatsAnnualDataSeriesRequestBuilder(): WMOccupationStatsRequestBuilder<WMOccupationStatsAnnualDataSeriesRequestBuilder>(
    "annual_series_list"
) {
    override fun self(): WMOccupationStatsAnnualDataSeriesRequestBuilder = this
}

class WMOccupationStatsAnnualChartRequestBuilder(): WMOccupationStatsRequestBuilder<WMOccupationStatsAnnualChartRequestBuilder>(
    "annual_chart"
) {
    override fun self(): WMOccupationStatsAnnualChartRequestBuilder = this
}
